import { useRef, useEffect } from 'react';
import debounce from 'lodash/debounce';
import useResizeObserver from '@tcp/web/src/hooks/useResizeObserver';
import { isClient } from '@tcp/core/src/utils';
import {
  DEFAULT_TRANSITION_DURATION,
  DEFAULT_TRANSITION_FUNCTION,
  EVENT_TRANSITION_END,
  FADE_OUT_OPACITY,
  FULL_OPACITY,
  HEIGHT_AUTO,
  OVERFLOW_Y_HIDDEN,
  RESIZE_OBSERVER_DEBOUNCE_TIME,
  TRANSITION_PROPERTY,
  UNSET,
} from './useAccordionTransition.constants';
import { getFinalHeightForTransition, setStylesToRef } from './useAccordionTransition.utils';

/**
 * Hook to apply expand/collapse transition on SinglePageCheckout sections.
 *
 * Step 1: With first animation frame (initialAnimationFrameId), set height to absolute value.
 *
 * Step 2: With second animation frame (finalAnimationFrameId), change height to estimated expand/collapse height of section.
 *
 * Step 3: Once transition ends, it will reset height to auto.
 */
const useAccordionTransition = ({ isExpanded, recalculateHeight, ...transitionConfig }) => {
  const {
    shouldUseTransition,
    transitionDuration = DEFAULT_TRANSITION_DURATION,
    transitionFunction = DEFAULT_TRANSITION_FUNCTION,
  } = transitionConfig;

  // Section container ref to measure current height of section
  const containerRef = useRef();
  // 1st animation frame for step 1
  const initialAnimationFrameId = useRef(null);
  // 2nd animation frame for step 1
  const finalAnimationFrameId = useRef(null);
  // Stores first render done
  const checkFirstRenderDone = useRef(false);
  const previousHeight = useRef(null);

  // Store previous rendering height
  const storePreviousHeight = () => {
    setTimeout(() => {
      previousHeight.current = containerRef?.current?.scrollHeight;
    }, 0);
  };

  // Reset transition styles when transition ends
  const resetTransitionStyles = () => {
    setStylesToRef(containerRef, {
      overflowY: UNSET,
      height: HEIGHT_AUTO,
      opacity: FULL_OPACITY,
      transition: UNSET,
    });

    storePreviousHeight();
  };

  /**
   * Cleanup animation
   */
  const cleanupAnimation = () => {
    if (initialAnimationFrameId.current) {
      // Cleanup initialAnimationFrame
      cancelAnimationFrame(initialAnimationFrameId.current);
    }
    if (finalAnimationFrameId.current) {
      // Cleanup finalAnimationFrame
      cancelAnimationFrame(finalAnimationFrameId.current);
    }
    if (containerRef?.current) {
      // Cleanup event listener for event transition
      containerRef.current.removeEventListener(EVENT_TRANSITION_END, resetTransitionStyles);
    }
  };

  // Set transition styles for final animation frame
  const setFinalTransitionStyles = () => {
    const finalHeight = getFinalHeightForTransition(isExpanded, transitionConfig);
    setStylesToRef(containerRef, {
      overflowY: OVERFLOW_Y_HIDDEN,
      height: `${finalHeight}px`,
      opacity: FADE_OUT_OPACITY,
      transition: `${TRANSITION_PROPERTY} ${transitionDuration} ${transitionFunction}`,
    });
  };

  // Initialize transition styles in first animation frame (set height to measured current height)
  const setInitialTransitionStyles = () => {
    setStylesToRef(containerRef, {
      overflowY: OVERFLOW_Y_HIDDEN,
      height: `${previousHeight.current}px`,
      opacity: FULL_OPACITY,
      transition: UNSET,
    });
    // Start final animation frame
    finalAnimationFrameId.current = requestAnimationFrame(setFinalTransitionStyles);
  };

  useEffect(() => {
    // Run transition only when
    // isClient
    // allowed in transitionConfig for section (shouldUseTransition)
    // And first render has already been done (section animation on first render does not look good)
    if (isClient() && shouldUseTransition) {
      if (checkFirstRenderDone.current) {
        // Add event listener for transitionend so reset is called when transition ends
        containerRef.current.addEventListener(EVENT_TRANSITION_END, resetTransitionStyles);
        initialAnimationFrameId.current = requestAnimationFrame(setInitialTransitionStyles);
      } else {
        storePreviousHeight();
      }
    }
    checkFirstRenderDone.current = true;
    return cleanupAnimation;
  }, [isExpanded]);

  // Storing previous height on every accordion card layout change
  useResizeObserver(containerRef, debounce(storePreviousHeight, RESIZE_OBSERVER_DEBOUNCE_TIME));

  return [containerRef];
};

export default useAccordionTransition;
